import { useRouter, type Href } from 'expo-router';
import { Dna, Hospital } from 'lucide-react-native';
import React, { useState } from 'react';
import { Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { twJoin } from 'tailwind-merge';

import type { ProductDirectoryCardProps } from './types';

import { primum_default_avatar } from '@/assets/images';
import { ProductSupportBadge } from '@/components/MedicalOnDemand/ProductSupportBadge';
import { WhenToUseExplainer } from '@/components/MedicalOnDemand/WhenToUseExplainer';
import { TrackedLink as TrackedRouterLink } from '@/components/TrackedLink';
import { ResponseTime } from '@/components/responseTime';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonGroup, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { NewBadge } from '@/components/ui/new-badge';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import {
  getProductSupportTeam,
  MOD_NEW_UNTIL,
  WHEN_TO_USE_EXPLAINER,
} from '@/constants/medicalOnDemand';
import { ROUTES } from '@/constants/routes';
import { useNewBadgePulse } from '@/hooks/useNewBadgePulse';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { segmentClient } from '@/util/analytics';
import { getNativeTextProps } from '@/util/getNativeTextProps';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';

const isWeb = Platform.OS === 'web';

const RESPONSE_TIME_LABEL = '~1 business day response time';

type CardContentProps = {
  item: ExpertProfile;
  isMultiColumn: boolean;
  showExplainerAffordances?: boolean;
};

/**
 * Internal card content component for ProductDirectoryCard.
 * Renders product support expert info with success badge and fixed response time.
 * Memoized to prevent unnecessary re-renders during list scrolling.
 */
const CardContent = React.memo(function CardContent({
  item,
  isMultiColumn,
  showExplainerAffordances,
}: CardContentProps) {
  const [isExplainerOpen, setIsExplainerOpen] = useState(false);

  const team = getProductSupportTeam(String(item.id));

  /**
   * Two conditions, as on the section: the date closes the window for everyone
   * at once, and the team check keeps the marker off any product-support card
   * added later, which would not be new just because this component renders it.
   */
  const showNewBadge =
    Boolean(showExplainerAffordances) && Boolean(team) && Date.now() < MOD_NEW_UNTIL.getTime();

  const newBadgePulse = useNewBadgePulse({ replayKey: String(item.id) });

  const { navigate: navigateFromCard } = useRouter();

  const { navigate } = useRouter();

  return (
    <Card
      variant="outline"
      testID="product-card"
      className={twJoin(isMultiColumn && 'hover:shadow-hard-1', 'p-0 py-4 web:h-full web:flex-1')}>
      <HStack className="items-center gap-6 px-4 web:gap-3">
        <Avatar size="md">
          <AvatarImage source={item.avatarSrc ? { uri: item.avatarSrc } : primum_default_avatar} />
        </Avatar>
        <VStack className="flex-1 justify-center" space="xs">
          <HStack className="items-center gap-2">
            <Text
              size={isMultiColumn ? 'md' : 'lg'}
              className="flex-1 font-medium text-typography-900"
              {...getNativeTextProps()}
              isTruncated>
              {item.name}
            </Text>
            {showNewBadge ? (
              <Animated.View ref={newBadgePulse.ref} style={newBadgePulse.style}>
                <NewBadge surface="card" />
              </Animated.View>
            ) : null}
          </HStack>

          {showExplainerAffordances ? (
            <WhenToUseExplainer
              isOpen={isExplainerOpen}
              surface="card"
              isNewBadgeVisible={showNewBadge}
              team={team}
              onClose={() => setIsExplainerOpen(false)}
              onAskTeam={() => {
                setIsExplainerOpen(false);

                navigateFromCard(`/medical-on-demand/inquiries/${item.id}` as Href);
              }}
              onAskExpert={() => {
                setIsExplainerOpen(false);

                navigateFromCard(ROUTES.INQUIRY);
              }}
            />
          ) : null}
          {/*
            The shared badge rather than the speciality string this used to
            render. `item.speciality` is a backend classification that happens
            to read the same today, so the label could drift from
            `TREATMENT_SUPPORT_LABEL` without anything failing. It also drew
            from the success ramp, which means positive outcome rather than
            classification, and left this badge a different green from the one
            on the composer it links to.
          */}
          <ProductSupportBadge className="self-start" />

          {/*
            On desktop the whole card is wrapped in a `Link`, so this press has
            to stop there or it opens the modal and navigates away underneath
            it. The modal's own content is safe without this: the overlay
            portals it outside the card's anchor.
          */}
          {showExplainerAffordances ? (
            <Pressable
              className="self-start web:cursor-pointer"
              hitSlop={ICON_BUTTON_HIT_SLOP}
              onPress={(event) => {
                event.preventDefault();

                event.stopPropagation();

                setIsExplainerOpen(true);
              }}
              {...getAccessibilityProps({
                label: WHEN_TO_USE_EXPLAINER.linkLabel,
                role: 'button',
                hint: 'Opens a guide to choosing between a Primum expert and the manufacturer',
              })}>
              <Text size="sm" className="font-medium text-primary-600 underline">
                {WHEN_TO_USE_EXPLAINER.linkLabel}
              </Text>
            </Pressable>
          ) : null}
        </VStack>
      </HStack>
      <Divider className="my-4" />
      <VStack className="mb-4 px-4">
        <VStack className="gap-2">
          {item.description && (
            <Text className="text-sm text-typography-700">{item.description}</Text>
          )}
          {item.institution ? (
            <HStack className="items-start gap-2">
              <Icon
                as={Hospital}
                className="shrink-0 text-typography-600"
                {...({ accessibilityLabel: 'Institution' } as any)}
              />
              <Text className="text-sm text-typography-700">{item.institution}</Text>
            </HStack>
          ) : null}
          <HStack className="items-start gap-2">
            <Icon
              as={Dna}
              className="shrink-0 text-typography-600"
              {...({ accessibilityLabel: 'Treatment' } as any)}
            />
            <Text className="text-sm text-typography-700">
              Treatments for: <Text className="font-medium">{item.focusArea || 'MM'}</Text>
            </Text>
          </HStack>
        </VStack>
      </VStack>
      {/*
       * Indented past the card's own gutter to the text column of the icon-led rows above,
       * since the response time carries no leading mark to sit in the icon slot. Stated as
       * one left margin rather than layered over the gutter, because two margin-left
       * declarations resolve by stylesheet order rather than by the order written.
       */}
      <VStack className="ml-11 mr-4">
        <ResponseTime
          responseTime={item.responseTime?.toString()}
          responseTimeLabel={RESPONSE_TIME_LABEL}
          fontSize="text-xs"
        />
      </VStack>

      <ButtonGroup className="mt-4 flex-row justify-center gap-4 px-4 md:hidden">
        <Button
          size="xs"
          onPress={(e) => {
            segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTED, {
              expert_id: item.id,
            });

            navigate(`/medical-on-demand/inquiries/${item.id}`);

            e.preventDefault();
          }}
          variant="outline"
          className="flex-1 web:md:hidden">
          <ButtonText>View Profile</ButtonText>
        </Button>
        <Button
          size="xs"
          onPress={(e) => {
            segmentClient.track(ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED, {
              expert_id: item.id,
              expert_name: item.name,
            });

            navigate(`/medical-on-demand/inquiries/${item.id}`);

            e.preventDefault();
          }}
          className="flex-1 web:flex-auto">
          <ButtonText>Message</ButtonText>
        </Button>
      </ButtonGroup>
    </Card>
  );
});

/**
 * ProductDirectoryCard - Complete card for product support experts
 *
 * Features:
 * - Success badge for speciality
 * - Treatments display (MM)
 * - Hardcoded ~1 business day response time
 * - Medical-on-demand navigation
 * - Platform-specific link wrapping for multi-column layout
 */
export const ProductDirectoryCard = React.memo(function ProductDirectoryCard({
  item,
  isMultiColumn,
  showExplainerAffordances,
}: ProductDirectoryCardProps) {
  const content = (
    <CardContent
      item={item}
      isMultiColumn={isMultiColumn}
      showExplainerAffordances={showExplainerAffordances}
    />
  );

  if (isMultiColumn) {
    const href = `/medical-on-demand/inquiries/${item.id}` as Href;

    return (
      <TrackedRouterLink
        asChild
        href={href}
        trackingEvent={ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED}
        trackingData={{ expert_id: item.id, expert_name: item.name }}>
        <Pressable className={twJoin('h-full', isWeb && 'flex-1 justify-between')}>
          {content}
        </Pressable>
      </TrackedRouterLink>
    );
  }

  return content;
});
